import React, { useMemo } from 'react';

const COLORS = ['#38bdf8','#818cf8','#34d399','#fb923c','#f472b6','#a78bfa','#fbbf24','#2dd4bf','#f87171','#e879f9'];

function highlight(text, query) {
  if (!text || !query) return text;
  const q = query.toLowerCase();
  const str = String(text);
  const lower = str.toLowerCase();
  const parts = [];
  let i = 0;
  while (i < str.length) {
    const idx = lower.indexOf(q, i);
    if (idx === -1) { parts.push(str.slice(i)); break; }
    if (idx > i) parts.push(str.slice(i, idx));
    parts.push(
      <mark key={idx} style={{ background: 'rgba(251,191,36,0.35)', color: '#fbbf24', padding: '0 2px', borderRadius: 2, fontWeight: 700 }}>
        {str.slice(idx, idx + q.length)}
      </mark>
    );
    i = idx + q.length;
  }
  return <>{parts}</>;
}

function searchData(data, query) {
  if (!query.trim() || !data) return [];
  const q = query.toLowerCase();
  const tables = Array.isArray(data) ? data : (data.tables || []);
  const results = [];

  tables.forEach((t, idx) => {
    const tableName = t.table_name || t.name || '';
    const tableDesc = t.description || '';
    const tableNameHit = tableName.toLowerCase().includes(q);
    const tableDescHit = tableDesc.toLowerCase().includes(q);

    const columnMatches = [];
    (t.columns || t.cols || []).forEach(c => {
      const fields = [];
      const cName  = (c.name || c.n || '');
      const cType  = (c.data_type || c.type || c.t || '');
      const cDesc  = (c.description || '');
      const cFmt   = (c.format || '');
      const cHint  = (c.relationship_hint || c.fk || '');

      if (cName.toLowerCase().includes(q)) fields.push('name');
      if (cType.toLowerCase().includes(q)) fields.push('type');
      if (cDesc.toLowerCase().includes(q)) fields.push('description');
      if (cFmt.toLowerCase().includes(q))  fields.push('format');
      if (String(cHint).toLowerCase().includes(q)) fields.push('relationship');

      if (fields.length) {
        columnMatches.push({
          name: cName, type: cType, description: cDesc, format: cFmt,
          relationship: cHint, matchedFields: fields,
        });
      }
    });

    if (tableNameHit || tableDescHit || columnMatches.length) {
      results.push({
        tableName, tableDesc, tableNameHit, tableDescHit,
        columns: columnMatches, colorIdx: idx,
      });
    }
  });

  return results;
}

export default function SearchView({ data, query }) {
  const results = useMemo(() => searchData(data, query), [data, query]);

  if (!data) return (
    <div style={{ padding: 40, color: 'var(--muted)', fontSize: 12 }}>
      Select a dataset from the Datasets tab before searching.
    </div>
  );

  if (!query.trim()) return (
    <div style={{ padding: 40, color: 'var(--muted)', fontSize: 12 }}>
      Type a search term in the sidebar to search across table names, columns, types, and descriptions.
    </div>
  );

  const totalMatches = results.reduce((sum, r) => sum + r.columns.length + (r.tableNameHit ? 1 : 0) + (r.tableDescHit ? 1 : 0), 0);

  return (
    <div style={{ height: '100%', overflow: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 6 }}>
        <h2 style={{ fontFamily: 'Syne, sans-serif', fontSize: 14, fontWeight: 700, letterSpacing: '.05em' }}>
          Search Results
        </h2>
        <span style={{ color: 'var(--muted)', fontSize: 11 }}>
          for "<span style={{ color: 'var(--accent)', fontWeight: 600 }}>{query}</span>"
        </span>
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <span className="tag">{results.length} {results.length === 1 ? 'table' : 'tables'}</span>
        <span className="tag">{totalMatches} matches</span>
      </div>

      {results.length === 0 ? (
        <div className="card" style={{ color: 'var(--muted)', fontSize: 12 }}>
          No matches found. Try a shorter or different search term.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {results.map(r => {
            const color = COLORS[r.colorIdx % COLORS.length];
            return (
              <div key={r.tableName} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ padding: '12px 16px', borderLeft: `3px solid ${color}`, borderBottom: r.columns.length || r.tableDescHit ? '1px solid var(--border)' : 'none' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 13, color, letterSpacing: '.03em' }}>
                      {r.tableNameHit ? highlight(r.tableName, query) : r.tableName}
                    </span>
                    {r.tableNameHit && <span className="tag" style={{ borderColor: 'rgba(251,191,36,0.4)', color: '#fbbf24', background: 'rgba(251,191,36,0.08)' }}>table name match</span>}
                    <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--muted)' }}>
                      {r.columns.length} {r.columns.length === 1 ? 'field' : 'fields'}
                    </span>
                  </div>
                  {r.tableDesc && (
                    <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4, fontStyle: 'italic' }}>
                      {r.tableDescHit ? highlight(r.tableDesc, query) : r.tableDesc}
                    </div>
                  )}
                </div>

                {r.columns.length > 0 && (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                    <thead>
                      <tr style={{ background: 'var(--row-overlay)' }}>
                        {['Column','Type','Matched in','Notes'].map(h => (
                          <th key={h} style={{ padding: '7px 14px', textAlign: 'left', fontSize: 9, color: 'var(--muted)', letterSpacing: '.06em', fontWeight: 700, borderBottom: '1px solid var(--border)' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {r.columns.map((c, i) => (
                        <tr key={i} style={{ borderBottom: i < r.columns.length - 1 ? '1px solid var(--border)' : 'none' }}>
                          <td style={{ padding: '7px 14px', fontWeight: 600, color: 'var(--text)' }}>
                            {c.matchedFields.includes('name') ? highlight(c.name, query) : c.name}
                          </td>
                          <td style={{ padding: '7px 14px', color: 'var(--muted)', fontSize: 10 }}>
                            {c.matchedFields.includes('type') ? highlight(c.type, query) : c.type}
                          </td>
                          <td style={{ padding: '7px 14px' }}>
                            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                              {c.matchedFields.map(f => (
                                <span key={f} style={{ fontSize: 8, padding: '1px 6px', borderRadius: 3, background: 'rgba(251,191,36,0.1)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.3)', fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase' }}>{f}</span>
                              ))}
                            </div>
                          </td>
                          <td style={{ padding: '7px 14px', color: 'var(--muted)', fontSize: 10, maxWidth: 320 }}>
                            {c.description && (
                              <div>{c.matchedFields.includes('description') ? highlight(c.description, query) : c.description}</div>
                            )}
                            {c.relationship && (
                              <div style={{ marginTop: 2 }}>
                                → <span style={{ color: 'var(--fk)' }}>{c.matchedFields.includes('relationship') ? highlight(c.relationship, query) : c.relationship}</span>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
